import {
  memo,
  ReactNode,
  TouchEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ConfigProvider, Flex, notification, ThemeConfig } from 'antd';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import Alert from 'components/Alert';
import LazySvg from 'components/LazySvg';
import useIsMobile from 'hooks/useIsMobile';
import { resetToastAlert } from 'services/toast-alert/toastAlertSlice';
import selectToastAlert from 'services/toast-alert/toastAlertSelectors';
import { ToastAlertSliceType } from 'services/toast-alert/types';
import { QaCommonComponent } from 'testdata/constants';
import { GENERAL } from 'utils/constants';
import TOAST_IMG_MAP from './data';
import styles from './global-toast-alert.module.scss';

/** Auto-dismiss durations in seconds (PI-12153). `info` follows success. */
const DURATION_S: Record<ToastAlertSliceType['status'], number> = {
  success: 4,
  info: 4,
  error: 8,
};

/** A single fixed key: opening a new toast replaces the one on screen. */
const TOAST_KEY = 'global-toast';

/** Upward drag distance (px) that dismisses the toast on touch. */
const SWIPE_DISMISS_PX = 32;

/** Gap (px) between the header's bottom edge and the toast (Figma 1:8239 / 1:8244). */
const TOP_OFFSET = { desktop: 24, mobile: 2 };

/**
 * - `marginLG` → antd's edge margin (`notificationMarginEdge`): 24 / 16.
 * - `width` → content-sized on desktop (capped in the module SCSS); on mobile
 *   `100vw` is clamped by antd's own `calc(100vw - 2 × edge)` max-width, so
 *   the toast spans the screen with the 16px side gutters.
 * - `margin` → `notificationMarginBottom` (single toast, no stacking gap).
 */
const getNotificationTheme = (isMobile: boolean): ThemeConfig => ({
  components: {
    Notification: {
      width: isMobile ? '100vw' : 'auto',
      colorBgElevated: 'transparent',
      boxShadow: 'var(--box-shadow)',
      borderRadiusLG: 12,
      paddingMD: 0,
      paddingContentHorizontalLG: 0,
      margin: 0,
      marginLG: isMobile ? 16 : 24,
    },
  },
});

type SwipeToDismissProps = {
  children: ReactNode;
  onDismiss: () => void;
};

/** Mobile: drag the toast upwards to dismiss it (PI-12153). */
function SwipeToDismiss({ children, onDismiss }: SwipeToDismissProps) {
  const startY = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    startY.current = event.touches[0].clientY;
  };

  const handleTouchMove = (event: TouchEvent<HTMLElement>) => {
    if (startY.current === null) return;
    // Upward only — a downward drag does nothing.
    setDragY(Math.min(0, event.touches[0].clientY - startY.current));
  };

  const handleTouchEnd = () => {
    startY.current = null;
    if (dragY <= -SWIPE_DISMISS_PX) {
      onDismiss();
      return;
    }
    setDragY(0);
  };

  return (
    <Flex
      vertical
      className={styles.swipe}
      style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {children}
    </Flex>
  );
}

function GlobalToastAlert() {
  const toastAlert = useAppSelector(selectToastAlert);
  const dispatch = useAppDispatch();
  const hostRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile(GENERAL.mobileSize);

  const [api, contextHolder] = notification.useNotification({
    placement: 'topRight',
    top: isMobile ? TOP_OFFSET.mobile : TOP_OFFSET.desktop,
    maxCount: 1,
    stack: false,
    pauseOnHover: true,
    getContainer: () => hostRef.current ?? document.body,
  });

  const dismiss = () => {
    dispatch(resetToastAlert());
  };

  useEffect(() => {
    if (!toastAlert) {
      api.destroy(TOAST_KEY);
      return;
    }

    const { status, message, icon } = toastAlert;
    api.open({
      key: TOAST_KEY,
      duration: DURATION_S[status] ?? DURATION_S.success,
      closable: false,
      role: status === 'error' ? 'alert' : 'status',
      message: (
        <SwipeToDismiss onDismiss={dismiss}>
          <Alert
            message={message}
            type={status}
            className={styles.alert}
            icon={<LazySvg name={icon ?? TOAST_IMG_MAP[status]} />}
            showIcon
            data-qa-selector={QaCommonComponent.alertGlobalToast}
          />
        </SwipeToDismiss>
      ),
      onClose: dismiss,
    });
  }, [toastAlert]);

  return (
    <div ref={hostRef} className={styles.host}>
      <ConfigProvider theme={getNotificationTheme(isMobile)}>
        {contextHolder}
      </ConfigProvider>
    </div>
  );
}

export default memo(GlobalToastAlert);
